<template>
  <WelcomeHeader />
  <GuidelineNav current="geography" />

  <!-- ═══ HERO ═══ -->
  <section class="hero-section">
    <div class="hero-noise"></div>
    <div class="container hero-inner">
      <div class="hero-badge">বিসিএস প্রস্তুতি গাইড · সংস্করণ ৪.০</div>
      <h1 class="hero-title">ভূগোল ও পরিবেশ <span class="hero-accent">গাইডলাইন</span></h1>
      <p class="hero-sub">বাংলাদেশ ও বিশ্ব — সম্পূর্ণ সিলেবাসভিত্তিক প্রস্তুতি গাইড</p>
      <div class="hero-stats">
        <div class="hstat"><div class="hstat-num">১০</div><div class="hstat-lbl">মোট মার্ক</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">০৫</div><div class="hstat-lbl">সিলেবাস অংশ</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">৩৫–৫০</div><div class="hstat-lbl">বিসিএস বিশ্লেষণ</div></div>
        <div class="hstat-sep"></div>
        <div class="hstat"><div class="hstat-num">৯ লাখ+</div><div class="hstat-lbl">অ্যাপ ডাউনলোড</div></div>
      </div>
    </div>
  </section>

  <!-- ═══ MARKS BREAKDOWN ═══ -->
  <div class="marks-bar">
    <div class="container">
      <div class="marks-header">মার্ক বিভাজন (মোট = ১০)</div>
      <div class="marks-grid">
        <div class="mk-item"><div class="mk-num">০২</div><div class="mk-lbl">ভৌগলিক অবস্থান, সীমানা ও মৌলিক ধারণা</div></div>
        <div class="mk-item"><div class="mk-num">০২</div><div class="mk-lbl">প্রাকৃতিক দুর্যোগ ও ব্যবস্থাপনা</div></div>
        <div class="mk-item"><div class="mk-num">০২</div><div class="mk-lbl">অঞ্চলভিত্তিক ভৌত পরিবেশ (ভূ-প্রাকৃতিক)</div></div>
        <div class="mk-item"><div class="mk-num">০২</div><div class="mk-lbl">বাংলাদেশের পরিবেশ: প্রকৃতি ও সম্পদ</div></div>
        <div class="mk-item"><div class="mk-num">০২</div><div class="mk-lbl">বাংলাদেশ ও বৈশ্বিক পরিবেশ পরিবর্তন</div></div>
      </div>
    </div>
  </div>

  <!-- ═══ MAIN ═══ -->
  <section class="main-section">
    <div class="container">

      <!-- Syllabus breakdown cards -->
      <div class="section-head">
        <h2>সিলেবাস বিশ্লেষণ — অংশভিত্তিক</h2>
        <p>প্রতিটি অংশের মান ০২ করে, সাথে গুরুত্বপূর্ণ সাব-টপিকসমূহ</p>
      </div>
      <div class="syllabus-list">
        <div v-for="part in syllabus" :key="part.n" class="syl-card" :class="'sp' + part.priority">
          <div class="syl-top">
            <div class="syl-num">{{ toBengali(part.n) }}</div>
            <div class="syl-title">{{ part.title }}</div>
            <div class="syl-mark">মান {{ part.mark }}</div>
          </div>
          <p class="syl-topics">{{ part.topics }}</p>
        </div>
      </div>

      <!-- Insight Cards -->
      <div class="insight-grid">
        <div class="ins-card must">
          <div class="ins-head">★★★ অবশ্যই পড়তে হবে</div>
          <ul>
            <li>বাংলাদেশের ভৌগলিক অবস্থান, সীমানা ও আয়তন</li>
            <li>ভূমিরূপ ও ভৌত পরিবেশ</li>
            <li>জলবায়ু ও পরিবেশ পরিবর্তন</li>
            <li>প্রকৃতি ও সম্পদ</li>
            <li>প্রাকৃতিক দুর্যোগ ও ব্যবস্থাপনা</li>
          </ul>
        </div>
        <div class="ins-card imp">
          <div class="ins-head">★★ গুরুত্বপূর্ণ</div>
          <ul>
            <li>পরিবেশ সম্মেলন ও চুক্তি (প্যারিস, কিয়োটো, রামসার)</li>
            <li>নদ-নদী ও বারিমণ্ডল</li>
            <li>বিশ্বের বৃহত্তম/ক্ষুদ্রতম ও ভৌগলিক উপনাম</li>
            <li>GIS, GPS, অক্ষরেখা ও দ্রাঘিমারেখা</li>
            <li>বাংলাদেশের সমুদ্র বিজয়</li>
          </ul>
        </div>
        <div class="ins-card low">
          <div class="ins-head">★ এক নজর দেখুন</div>
          <ul>
            <li>সম্পদের বণ্টন ও শিল্পোৎপাদনকারী দেশ</li>
            <li>অঞ্চলের নতুন ও পুরাতন নাম</li>
            <li>প্রশাসনিক কাঠামো</li>
            <li>দুর্যোগ ব্যবস্থাপনা ব্যুরো ইত্যাদি প্রাতিষ্ঠানিক তথ্য</li>
          </ul>
        </div>
      </div>

      <!-- Analysis -->
      <div class="analysis-box">
        <h4>📊 বিগত বছরের প্রশ্ন বিশ্লেষণ</h4>
        <p>ভূগোল (বাংলাদেশ ও বিশ্ব) এর বিগত বছরের প্রশ্ন বিশ্লেষণ করে দেখা যায় — বিশেষ করে <strong>৩৫ থেকে ৫০ বিসিএস</strong> পর্যন্ত — প্রশ্ন করার ক্ষেত্রে টপিক অনুসারে মানবণ্টন খুব কড়াকড়িভাবে ফলো করা হয় না।</p>
        <p>তবে <strong>বাংলাদেশের ভৌগোলিক অবস্থান, ভূমিরূপ, জলবায়ু, প্রকৃতি ও সম্পদ, প্রাকৃতিক দুর্যোগ ও ব্যবস্থাপনা</strong> — এই টপিকগুলো থেকে প্রায় প্রতিবছরই প্রশ্ন আসে। তাই এগুলো খুব ভালোভাবে অধ্যয়ন করতে হবে।</p>
        <p class="mb-0">বাকি টপিকগুলো থেকে ঘুরিয়ে-ফিরিয়ে প্রশ্ন আসে। এগুলো একদম বাদ না দিয়ে শুধুমাত্র গুরুত্বপূর্ণ তথ্যগুলো এক নজর দেখে রাখুন।</p>
      </div>
    </div>
  </section>

  <!-- ═══ FREE PDF OPT-IN ═══ -->
  <GuidelineOptin subject="geography" pdf="https://www.bcsprostuti.com/api5/storage/pdfs/BCS-Geography-Guideline-1_2026_06_20_14_23_22.pdf" />

  <!-- ═══ REFERENCE BOOKS ═══ -->
  <section class="ref-section">
    <div class="container">
      <div class="section-head center">
        <h2>রেফারেন্স বই</h2>
        <p>পরীক্ষায় ভালো করার জন্য এই বইগুলো অনুসরণ করুন</p>
      </div>
      <div class="book-grid">
        <div class="book-card" v-for="b in refBooks" :key="b.title">
          <div class="book-spine"></div>
          <div class="book-info">
            <div class="book-title">{{ b.title }}</div>
            <div class="book-author">{{ b.author }}</div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ═══ TIPS ═══ -->
  <section class="tips-section">
    <div class="container">
      <div class="section-head center">
        <h2>পরামর্শ ও কৌশল</h2>
        <p>কীভাবে ভূগোল ও পরিবেশে সর্বোচ্চ নম্বর তুলবেন</p>
      </div>
      <div class="tips-grid">
        <div class="tip-card" v-for="(tip, i) in tips" :key="i">
          <div class="tip-num">{{ toBengali(i + 1) }}</div>
          <p class="tip-text">{{ tip }}</p>
        </div>
      </div>
    </div>
  </section>

  <GuidelineRelated current="geography" />

  <!-- ═══ CTA ═══ -->
  <section class="cta-section">
    <div class="container">
      <div class="cta-grid">
        <div class="cta-left">
          <div class="cta-tag">বিসিএস প্রস্তুতি অ্যাপ</div>
          <h2 class="cta-title">ভূগোল ও পরিবেশে সর্বোচ্চ নম্বর তুলুন</h2>
          <ul class="cta-features">
            <li><span class="cf-icon">✓</span> টপিকভিত্তিক প্রশ্ন ব্যাঙ্ক ও ব্যাখ্যা</li>
            <li><span class="cf-icon">✓</span> বিগত ৩৫–৫০ বিসিএসের সম্পূর্ণ প্রশ্নসমূহ</li>
            <li><span class="cf-icon">✓</span> স্মার্ট প্রগ্রেস ট্র্যাকিং সিস্টেম</li>
            <li><span class="cf-icon">✓</span> লাইভ কোর্স ও মডেল টেস্ট</li>
          </ul>
          <div class="cta-actions">
            <a href="https://play.google.com/store/apps/details?id=com.bcsprostuti.tanim.bcsprostuti" target="_blank" class="cta-play" @click="trackAppInstall">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" alt="Google Play" height="48">
            </a>
            <a href="/packages" class="cta-pkg" @click="trackInitiateCheckout">প্যাকেজ দেখুন →</a>
          </div>
          <p class="cta-count">৯ লাখ+ শিক্ষার্থীর বিশ্বাসের অ্যাপ</p>
        </div>
        <div class="cta-right">
          <div class="cta-stat-card"><div class="cs-num">১০</div><div class="cs-lbl">মোট মার্ক</div></div>
          <div class="cta-stat-card"><div class="cs-num">৩৫–৫০</div><div class="cs-lbl">বিসিএস বিশ্লেষণ</div></div>
          <div class="cta-stat-card"><div class="cs-num">০৫</div><div class="cs-lbl">সিলেবাস অংশ</div></div>
          <div class="cta-stat-card"><div class="cs-num">৯ লাখ+</div><div class="cs-lbl">অ্যাপ ডাউনলোড</div></div>
        </div>
      </div>
    </div>
  </section>

  <WelcomeFooter :hide-download="true" />

  <MessengerFloat subject="geography" />
</template>

<script>
import WelcomeHeader from "@/components/WelcomePages/WelcomeHeader.vue";
import WelcomeFooter from "@/components/WelcomePages/WelcomeFooter.vue";
import GuidelineNav from "@/components/WelcomePages/GuidelineNav.vue";
import GuidelineRelated from "@/components/WelcomePages/GuidelineRelated.vue";
import GuidelineOptin from "@/components/WelcomePages/GuidelineOptin.vue";
import MessengerFloat from "@/components/WelcomePages/MessengerFloat.vue";
import * as pixels from "@/config/pixels";
import { onMounted } from 'vue';

export default {
  components: { WelcomeHeader, WelcomeFooter, GuidelineNav, GuidelineRelated, GuidelineOptin, MessengerFloat },
  setup() {
    // ভূগোল সিলেবাস V3 ডক থেকে — প্রতিটি অংশ মান ০২, মোট ১০।
    // (ভূগোলে টপিকভিত্তিক মানবণ্টন কড়াকড়িভাবে ফলো হয় না, তাই হিটম্যাপের
    //  বদলে সিলেবাসভিত্তিক বিশ্লেষণ দেখানো হয়েছে।)
    const syllabus = [
      {
        n: 1,
        title: 'বাংলাদেশ ও অঞ্চলভিত্তিক ভৌগলিক অবস্থান ও সীমানা',
        mark: '০২',
        priority: 3,
        topics: 'ভূগোলের শাখা, GIS, GPS, অক্ষরেখা, দ্রাঘিমারেখা ও গুরুত্বপূর্ণ রেখাসমূহ; বাংলাদেশের ভৌগলিক অবস্থান, আয়তন ও সীমানা; বাংলাদেশের সমুদ্র বিজয়; প্রশাসনিক কাঠামো; অঞ্চলের নতুন ও পুরাতন নাম; মহাদেশ এবং মহাদেশভিত্তিক বৃহত্তম ও ক্ষুদ্রতম।',
      },
      {
        n: 2,
        title: 'প্রাকৃতিক দুর্যোগ ও ব্যবস্থাপনা',
        mark: '০২',
        priority: 3,
        topics: 'দুর্যোগের ধরন, প্রকৃতি ও ব্যবস্থাপনা; প্রাকৃতিক দুর্যোগসমূহ; ঘূর্ণিঝড় সতর্কীকরণ ও সমুদ্র সংকেত; উল্লেখযোগ্য মানবসৃষ্ট দুর্যোগ; দুর্যোগ ব্যবস্থাপনা চক্র; উপকূলীয় দুর্যোগ ব্যবস্থাপনা; দুর্যোগ ব্যবস্থাপনা ব্যুরো ইত্যাদি।',
      },
      {
        n: 3,
        title: 'অঞ্চলভিত্তিক ভৌত পরিবেশ (ভূ-প্রাকৃতিক)',
        mark: '০২',
        priority: 2,
        topics: 'পৃথিবীর অভ্যন্তরীণ গঠন (ভূত্বক, গুরুমণ্ডল, কেন্দ্রমণ্ডল), খনিজ ও শিলা; বাহ্যিক গঠন (পর্বত, পর্বতশ্রেণী, মালভূমি, সমভূমি, মরুভূমি); ভূ-পৃষ্ঠের পরিবর্তন প্রক্রিয়া (নদী ও নদীসৃষ্ট ভূমিরূপ, প্লাবন সমভূমি, ব-দ্বীপ); বারিমণ্ডল (মহাসাগর, সাগর, উপসাগর, হ্রদ, সমুদ্রবন্দর, দ্বীপ); সমুদ্র তলদেশের ভূমিরূপ (মহীসোপান, মহীঢাল); সমুদ্রস্রোত, প্রণালী, খাল, বিখ্যাত নদ-নদী, অন্তরীপ; বিশ্বের বৃহত্তম/ক্ষুদ্রতম/উচ্চতম/দীর্ঘতম ও ভৌগলিক উপনাম; সম্পদের বণ্টন ও গুরুত্ব (শীর্ষ শিল্পোৎপাদনকারী, উৎপাদন-রপ্তানি-আমদানিতে শীর্ষ দেশ)।',
      },
      {
        n: 4,
        title: 'বাংলাদেশের পরিবেশ: প্রকৃতি ও সম্পদ',
        mark: '০২',
        priority: 3,
        topics: 'বাংলাদেশের ভূপ্রকৃতি; টারশিয়ারি যুগের পাহাড়সমূহ; প্লাইস্টোসিনকালের সোপানসমূহ; সাম্প্রতিককালের প্লাবন সমভূমি; নদ-নদী ও উৎপত্তিস্থল, উপনদী ও শাখানদী; বন্দর, হ্রদ, বিল, সমুদ্র সৈকত, দ্বীপ, পাহাড়-পর্বত; পরিবেশ দূষণ (বায়ু, শব্দ, পানি, মাটি) ও পরিবেশ সংরক্ষণ; প্রধান চ্যালেঞ্জসমূহ।',
      },
      {
        n: 5,
        title: 'বাংলাদেশ ও বৈশ্বিক পরিবেশ পরিবর্তন',
        mark: '০২',
        priority: 2,
        topics: 'আবহাওয়া ও জলবায়ু নিয়ামকসমূহ — সেক্টরভিত্তিক প্রভাব (অভিবাসন, কৃষি, শিল্প, মৎস্য); স্থানীয়, আঞ্চলিক ও বৈশ্বিক প্রভাব; পরিবেশ সংক্রান্ত দিবস, সম্মেলন ও উদ্যোগ — IPCC, UNEP, COP, ধরিত্রী সম্মেলন, কিয়োটো প্রোটোকল, সবুজ জলবায়ু তহবিল (GCF), প্যারিস চুক্তি, গ্রিনপিস, রামসার কনভেনশন, কার্টাগেনা প্রটোকল, ওজোনস্তর অবক্ষয়, ভিয়েনা কনভেনশন, মন্ট্রিল প্রটোকল।',
      },
    ];

    const refBooks = [
      { title: 'ভূগোল (৯ম-১০ম শ্রেণি)', author: 'বোর্ড বই' },
      { title: 'বাংলাদেশ ও বিশ্বপরিচয় (৯ম-১০ম শ্রেণি — ৩য়, ৪র্থ, ৫ম অধ্যায়)', author: 'বোর্ড বই' },
      { title: 'বাংলাদেশ ও বিশ্বপরিচয় (৬ষ্ঠ–৮ম শ্রেণি — নির্দিষ্ট অধ্যায়)', author: 'বোর্ড বই' },
      { title: 'বিজ্ঞান (৬ষ্ঠ–১০ম শ্রেণি — নির্দিষ্ট অধ্যায়)', author: 'বোর্ড বই' },
      { title: 'উচ্চ মাধ্যমিক ভূগোল ১ম পত্র', author: 'বোর্ড বই' },
      { title: 'worldatlas.com, britannica.com, statista.com', author: 'অনলাইন রিসোর্স (প্রাসঙ্গিক তথ্য)' },
      { title: 'যে কোনো এক সেট গাইড বই', author: 'নির্দেশনার জন্য' },
      { title: 'বিসিএস প্রস্তুতি অ্যাপ — ভূগোল সেকশন', author: 'টপিকভিত্তিক প্রশ্ন ও ব্যাখ্যা' },
    ];

    const tips = [
      'প্রথমেই বিসিএস প্রস্তুতি অ্যাপ থেকে ভূগোলের বিগত বছরের প্রশ্নগুলো ব্যাখ্যাসহ পড়ে ফেলুন — কী ধরনের প্রশ্ন আসে সে সম্পর্কে একটা সামগ্রিক ধারণা পাবেন, তখন বুঝতে পারবেন কী কী পড়া লাগবে আর কী লাগবে না।',
      'বিগত বছরের প্রশ্ন বিশ্লেষণ ভালোভাবে দেখুন এবং সেইভাবে বেসিক থেকে পড়ুন। যেসব টপিক থেকে বেশি প্রশ্ন হয় সেগুলো গভীর থেকে পড়ুন, বাকিগুলোর শুধু গুরুত্বপূর্ণ তথ্য দেখুন — সব পড়ার দরকার নেই।',
      '৯ম-১০ম শ্রেণির বোর্ড বইটি বেসিক থেকে টপিক ধরে ধরে একবারে শেষ করে ফেলুন যেন কোনো ফাঁক না থাকে। এরপর PSC-র বিগত বছরের সকল প্রশ্ন ব্যাখ্যাসহ পড়ে ফেলুন।',
      'টপিক অনুসারে বেসিক থেকে পড়ুন। বিসিএস প্রস্তুতি অ্যাপে সম্পূর্ণ ভূগোল টপিক/সাব-টপিক অনুসারে সাজানো আছে — অ্যাপের স্মার্ট সিস্টেম আপনার প্রগ্রেস ট্র্যাক করবে।',
      'অনুশীলন শেষ হলে অ্যাপ থেকে যত খুশি বিষয়ভিত্তিক পরীক্ষা দিয়ে নিজের দুর্বলতা যাচাই করুন। মনে রাখবেন, বিসিএসে পরীক্ষা দেওয়ার কোনো বিকল্প নেই।',
      'কোনো টপিকে কনফিউশন হলে সার্চ অপশনে খুঁজে পড়ে ফেলুন, গুরুত্বপূর্ণ বা ভুল প্রশ্ন মার্ক করে রাখুন আর বার বার রিভিশন দিন। সাথে অ্যাপের যে কোনো একটি লাইভ কোর্স ভালোভাবে ফলো করুন।',
    ];

    // ── Helpers ──
    const toBengali = (n) => {
      const bn = ['০','১','২','৩','৪','৫','৬','৭','৮','৯'];
      return String(n).split('').map(c => bn[parseInt(c)] ?? c).join('');
    };

    // ── Funnel events (centralized in @/config/pixels) ──
    const trackAppInstall = () => pixels.trackAppInstallClick('geography');
    const trackInitiateCheckout = () => pixels.trackInitiateCheckout('geography');

    const setMeta = (attrs) => {
      const selector = attrs.property ? `meta[property="${attrs.property}"]` : `meta[name="${attrs.name}"]`;
      let el = document.querySelector(selector);
      if (!el) { el = document.createElement('meta'); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k,v]) => el.setAttribute(k,v));
    };

    onMounted(() => {
      window.scrollTo(0,0);
      document.title = 'ভূগোল ও পরিবেশ গাইডলাইন — বিসিএস সিলেবাস বিশ্লেষণ (৩৫–৫০ বিসিএস)';
      setMeta({name:'description', content:'বিসিএস ভূগোল ও পরিবেশের সম্পূর্ণ সিলেবাসভিত্তিক গাইডলাইন। ভৌগলিক অবস্থান, ভূমিরূপ, প্রাকৃতিক দুর্যোগ, প্রকৃতি-সম্পদ ও পরিবেশ পরিবর্তন — গুরুত্বপূর্ণ টপিক ও প্রস্তুতি কৌশল।'});
      setMeta({name:'keywords', content:'ভূগোল গাইডলাইন, পরিবেশ গাইডলাইন, BCS Geography, বিসিএস প্রস্তুতি, প্রাকৃতিক দুর্যোগ, ভূমিরূপ, জলবায়ু পরিবর্তন'});
      setMeta({name:'robots', content:'index, follow'});
      setMeta({property:'og:title', content:'ভূগোল ও পরিবেশ গাইডলাইন — বিসিএস সিলেবাস বিশ্লেষণ'});
      setMeta({property:'og:description', content:'বিসিএস ভূগোল ও পরিবেশের সম্পূর্ণ সিলেবাসভিত্তিক গাইডলাইন।'});
      setMeta({property:'og:url', content:'https://www.bcsprostuti.com/geography-guideline'});
      setMeta({property:'og:type', content:'article'});
      setMeta({property:'og:image', content:'https://www.bcsprostuti.com/og/geography-guideline.jpg'});
      setMeta({property:'og:image:width', content:'1200'});
      setMeta({property:'og:image:height', content:'630'});
      setMeta({property:'og:image:alt', content:'ভূগোল ও পরিবেশ গাইডলাইন'});
      setMeta({property:'og:site_name', content:'বিসিএস প্রস্তুতি'});
      setMeta({name:'twitter:card', content:'summary_large_image'});
      setMeta({name:'twitter:title', content:'ভূগোল ও পরিবেশ গাইডলাইন — বিসিএস সিলেবাস বিশ্লেষণ'});
      setMeta({name:'twitter:description', content:'বিসিএস ভূগোল ও পরিবেশের সম্পূর্ণ সিলেবাসভিত্তিক গাইডলাইন।'});
      setMeta({name:'twitter:image', content:'https://www.bcsprostuti.com/og/geography-guideline.jpg'});
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement('link'); canonical.rel='canonical'; document.head.appendChild(canonical); }
      canonical.href = 'https://www.bcsprostuti.com/geography-guideline';
      pixels.trackGuidelineView('geography');
      pixels.armViewedGuideline50('geography');
    });

    return { syllabus, refBooks, tips, toBengali, trackAppInstall, trackInitiateCheckout };
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }

.hero-section { position: relative; background: linear-gradient(140deg,#191B47 0%,#0d0f2e 55%,#06281f 100%); padding: 80px 0 60px; overflow: hidden; }
.hero-noise { position: absolute; inset: 0; pointer-events: none; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E"); }
.hero-inner { position: relative; text-align: center; }
.hero-badge { display: inline-block; background: rgba(0,149,255,0.15); border: 1px solid rgba(0,149,255,0.4); color: #0095FF; font-size: 0.8rem; font-weight: 600; padding: 6px 18px; border-radius: 999px; letter-spacing: 0.05em; margin-bottom: 20px; }
.hero-title { font-family: 'Noto Serif Bengali', serif; font-size: clamp(2rem,5vw,3.2rem); font-weight: 900; color: #fff; line-height: 1.25; margin-bottom: 16px; }
.hero-accent { background: linear-gradient(90deg,#0095FF,#00c6ff); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.hero-sub { font-size: 1.05rem; color: #94a3b8; margin-bottom: 40px; }
.hero-stats { display: flex; align-items: center; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); border-radius: 16px; padding: 16px 20px; backdrop-filter: blur(12px); flex-wrap: wrap; justify-content: center; row-gap: 12px; }
.hstat { text-align: center; padding: 0 16px; }
.hstat-num { font-family: 'Noto Serif Bengali', serif; font-size: clamp(1.3rem,3vw,2rem); font-weight: 900; color: #0095FF; line-height: 1; }
.hstat-lbl { font-size: 0.72rem; color: #94a3b8; margin-top: 4px; }
.hstat-sep { width: 1px; height: 40px; background: rgba(255,255,255,0.15); flex-shrink: 0; }
@media(max-width:768px) { .hstat-sep { display: none; } .hstat { padding: 4px 12px; } }

/* marks bar */
.marks-bar { background: #191B47; padding: 20px 0; }
.marks-header { font-size: 0.75rem; font-weight: 700; color: #94a3b8; text-align: center; margin-bottom: 14px; letter-spacing: 0.05em; text-transform: uppercase; }
.marks-grid { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
.mk-item { display: flex; flex-direction: column; align-items: center; background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12); border-radius: 10px; padding: 10px 16px; min-width: 90px; max-width: 200px; }
.mk-num { font-family: 'Noto Serif Bengali', serif; font-size: 1.3rem; font-weight: 800; color: #0095FF; line-height: 1; }
.mk-lbl { font-size: 0.68rem; color: #94a3b8; margin-top: 5px; text-align: center; }

/* main */
.main-section { padding: 40px 0; }

/* section head */
.section-head { margin-bottom: 20px; }
.section-head h2 { font-size: 0.95rem; font-weight: 700; color: #191B47; margin: 0 0 3px 0; border-left: 3px solid #0095FF; display: block; padding: 2px 0 2px 10px; }
.section-head p { color: #64748b; font-size: 0.8rem; margin: 0; padding-left: 13px; }
.section-head.center { text-align: center; }
.section-head.center h2 { border-left: none; padding-left: 0; font-size: 1.1rem; border-bottom: 3px solid #0095FF; display: inline-block; padding-bottom: 4px; }
.section-head.center p { padding-left: 0; margin-top: 8px; }

/* syllabus cards */
.syllabus-list { display: flex; flex-direction: column; gap: 14px; margin-bottom: 40px; }
.syl-card { background: #fff; border: 1px solid #e2e8f0; border-left: 4px solid #94a3b8; border-radius: 14px; padding: 20px 22px; box-shadow: 0 2px 12px rgba(0,0,0,0.05); text-align: left; }
.syl-card.sp3 { border-left-color: #DC2626; }
.syl-card.sp2 { border-left-color: #D97706; }
.syl-card.sp1 { border-left-color: #059669; }
.syl-top { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.syl-num { width: 30px; height: 30px; flex-shrink: 0; border-radius: 50%; background: #191B47; color: #fff; display: flex; align-items: center; justify-content: center; font-family: 'Noto Serif Bengali', serif; font-weight: 800; font-size: 0.9rem; }
.syl-title { flex: 1; font-size: 0.98rem; font-weight: 800; color: #191B47; line-height: 1.4; }
.syl-mark { flex-shrink: 0; font-size: 0.72rem; font-weight: 800; color: #0095FF; background: rgba(0,149,255,0.1); border: 1px solid rgba(0,149,255,0.25); padding: 4px 12px; border-radius: 999px; white-space: nowrap; }
.syl-topics { font-size: 0.88rem; color: #475569; line-height: 1.75; margin: 0; }
@media(max-width:560px) {
  .syl-top { flex-wrap: wrap; }
  .syl-title { flex: 1 1 100%; order: 3; }
  .syl-mark { order: 2; }
}

/* insight */
.insight-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; margin-bottom: 32px; }
@media(max-width:768px) { .insight-grid { grid-template-columns: 1fr; } }
.ins-card { border-radius: 14px; padding: 22px 20px; text-align: left; }
.ins-head { font-weight: 800; font-size: 0.9rem; margin-bottom: 14px; text-align: left; }
.ins-card ul { padding-left: 18px; margin: 0; text-align: left; }
.ins-card li { font-size: 0.88rem; margin-bottom: 7px; color: #374151; text-align: left; word-break: break-word; }
.ins-card.must { background: #fef2f2; border: 1px solid #fecaca; }
.ins-card.must .ins-head { color: #991b1b; }
.ins-card.imp { background: #fffbeb; border: 1px solid #fde68a; }
.ins-card.imp .ins-head { color: #92400e; }
.ins-card.low { background: #f0fdf4; border: 1px solid #bbf7d0; }
.ins-card.low .ins-head { color: #166534; }

/* analysis */
.analysis-box { background: #f0f9ff; border-left: 4px solid #191B47; border-radius: 0 14px 14px 0; padding: 20px 24px; margin-bottom: 8px; text-align: left; }
.analysis-box h4 { font-size: 1rem; font-weight: 700; color: #191B47; margin-bottom: 10px; }
.analysis-box p { font-size: 0.9rem; color: #374151; margin-bottom: 10px; line-height: 1.7; }
.mb-0 { margin-bottom: 0; }

/* ref books */
.ref-section { background: #f8fafc; padding: 48px 0; }
.book-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 12px; margin-top: 28px; width: 100%; }
@media(max-width:768px) { .book-grid { grid-template-columns: repeat(2,1fr); } }
@media(max-width:480px) { .book-grid { grid-template-columns: 1fr; } }
.book-card { display: flex; align-items: stretch; border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; background: #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.04); transition: transform 0.18s; }
.book-card:hover { transform: translateY(-2px); box-shadow: 0 6px 18px rgba(0,0,0,0.09); }
.book-spine { width: 5px; background: linear-gradient(180deg,#191B47,#065f46); flex-shrink: 0; }
.book-info { padding: 12px 14px; display: flex; flex-direction: column; justify-content: center; text-align: left; }
.book-title { font-size: 0.82rem; font-weight: 700; color: #1e293b; margin-bottom: 3px; line-height: 1.4; }
.book-author { font-size: 0.74rem; color: #64748b; }

/* tips */
.tips-section { background: #fff; padding: 48px 0; }
.tips-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; margin-top: 28px; }
@media(max-width:600px) { .tips-grid { grid-template-columns: 1fr; } }
.tip-card { display: flex; gap: 16px; align-items: flex-start; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 14px; padding: 20px; }
.tip-num { width: 32px; height: 32px; border-radius: 50%; background: #fff; color: #191B47; border: 2px solid #191B47; display: flex; align-items: center; justify-content: center; font-family: 'Noto Serif Bengali', serif; font-weight: 800; font-size: 0.85rem; flex-shrink: 0; }
.tip-text { font-size: 0.9rem; color: #374151; line-height: 1.7; margin: 0; text-align: left; }

/* CTA */
.cta-section { background: #191B47; border-top: 4px solid #0095FF; padding: 64px 0; }
.cta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
@media(max-width:768px) { .cta-grid { grid-template-columns: 1fr; gap: 32px; } }
.cta-left { align-self: center; }
.cta-tag { display: inline-block; font-size: 0.72rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #0095FF; background: rgba(0,149,255,0.15); border: 1px solid rgba(0,149,255,0.35); padding: 4px 14px; border-radius: 999px; margin-bottom: 14px; }
.cta-title { font-family: 'Noto Serif Bengali', serif; font-size: 1.5rem; font-weight: 800; color: #fff; line-height: 1.4; margin-bottom: 20px; }
.cta-features { list-style: none; padding: 0; margin: 0 0 24px 0; display: flex; flex-direction: column; gap: 9px; }
.cta-features li { display: flex; align-items: center; gap: 10px; font-size: 0.88rem; color: #c7d2fe; }
.cf-icon { width: 20px; height: 20px; background: rgba(0,149,255,0.2); color: #0095FF; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem; font-weight: 800; flex-shrink: 0; }
.cta-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.cta-play { display: inline-flex; align-items: center; background: #fff; border-radius: 10px; padding: 7px 16px; text-decoration: none; transition: transform 0.2s; }
.cta-play:hover { transform: scale(1.03); }
.cta-pkg { display: inline-flex; align-items: center; background: #0095FF; color: #1a1a1a; font-weight: 700; font-size: 0.9rem; border-radius: 10px; padding: 11px 24px; text-decoration: none; transition: all 0.2s; }
.cta-pkg:hover { background: #007AD6; transform: scale(1.02); }
.cta-count { font-size: 0.78rem; color: #64748b; margin: 0; }
.cta-right { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; align-self: center; }
@media(max-width:768px) { .cta-right { grid-template-columns: repeat(2, 1fr); gap: 10px; } }
.cta-stat-card { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.14); border-radius: 14px; padding: 24px 20px; text-align: center; }
.cs-num { font-family: 'Noto Serif Bengali', serif; font-size: 1.6rem; font-weight: 800; color: #0095FF; line-height: 1.1; }
.cs-lbl { font-size: 0.75rem; color: #94a3b8; margin-top: 7px; }
</style>